import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiCheckCircle, FiAlertCircle, FiPackage, FiArrowLeft, FiClock } = FiIcons;

const STATUS_FLOW = [
  { key: 'pending', label: 'Order Placed' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'packing', label: 'Packing' },
  { key: 'ready_to_ship', label: 'Ready to Ship' },
  { key: 'picked_up', label: 'Picked Up' },
  { key: 'delivered', label: 'Delivered' }
];

function PublicTracking() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const loadOrder = async () => {
      if (!token) {
        setError('Invalid tracking link.');
        setLoading(false);
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('orders_1750000001')
        .select('*')
        .eq('tracking_token', token)
        .limit(1)
        .single();

      if (fetchError || !data) {
        setError('No order found for this tracking link. The link may be invalid.');
        setLoading(false);
        return;
      }

      // Check if link expired (30 days after delivery)
      if (data.status === 'delivered' && data.delivered_at) {
        const deliveredDate = new Date(data.delivered_at);
        const now = new Date();
        const daysSinceDelivery = (now - deliveredDate) / (1000 * 60 * 60 * 24);
        if (daysSinceDelivery > 30) {
          setExpired(true);
          setOrder(data);
          setLoading(false);
          return;
        }
      }

      setOrder(data);

      const { data: itemsData } = await supabase
        .from('order_items_1750000001')
        .select('*')
        .eq('order_id', data.id);
      setItems(itemsData || []);

      setLoading(false);
    };

    loadOrder();
  }, [token]);

  const currentStepIndex = order
    ? STATUS_FLOW.findIndex((s) => s.key === order.status)
    : -1;

  if (loading) {
    return (
      <main className="public-tracking-page">
        <div className="section-shell public-tracking-loading">
          <div className="tracking-spinner" />
          <p>Loading your order details…</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="public-tracking-page">
        <div className="section-shell">
          <div className="public-tracking-error-card">
            <SafeIcon icon={FiAlertCircle} />
            <h2>Tracking Link Invalid</h2>
            <p>{error}</p>
            <Link to="/" className="button primary gradient-btn">
              <SafeIcon icon={FiArrowLeft} /> Go to Homepage
            </Link>
          </div>
        </div>
      </main>
    );
  }

  if (expired) {
    return (
      <main className="public-tracking-page">
        <div className="section-shell">
          <div className="public-tracking-expired-card">
            <SafeIcon icon={FiClock} />
            <h2>Tracking Link Expired</h2>
            <p>This tracking link has expired. Links are valid for 30 days after delivery.</p>
            <p className="expired-order-info">
              Your order <strong>#{order.id.slice(0, 8)}</strong> was delivered on{' '}
              {new Date(order.delivered_at).toLocaleDateString('en-GB', {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
              })}.
            </p>
            <Link to="/" className="button primary gradient-btn">
              <SafeIcon icon={FiArrowLeft} /> Go to Homepage
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="public-tracking-page">
      <div className="section-shell">
        <div className="public-tracking-header">
          <Link to="/"><SafeIcon icon={FiArrowLeft} /> Back to Home</Link>
          <h1>Order Tracking</h1>
          <p>Live status for order <strong>#{order.id.slice(0, 8)}</strong></p>
        </div>

        <div className="public-tracking-card">
          <div className="public-tracking-status-banner">
            <div className="status-icon-large">
              <SafeIcon icon={FiPackage} />
            </div>
            <div>
              <span className="current-status-label">Current Status</span>
              <h2>{STATUS_FLOW.find((s) => s.key === order.status)?.label || order.status}</h2>
              <span className="tracking-number-display-inline">{order.tracking_number}</span>
            </div>
          </div>

          <div className="public-tracking-progress">
            {STATUS_FLOW.map((step, i) => (
              <div
                key={step.key}
                className={`public-step ${i <= currentStepIndex ? 'completed' : ''} ${i === currentStepIndex ? 'current' : ''}`}
              >
                <div className="public-step-dot">
                  {i <= currentStepIndex
                    ? <SafeIcon icon={FiCheckCircle} />
                    : <span>{i + 1}</span>}
                </div>
                <span className="public-step-label">{step.label}</span>
                {i < STATUS_FLOW.length - 1 && <div className="public-step-line" />}
              </div>
            ))}
          </div>

          <div className="public-tracking-details">
            <div className="public-tracking-col">
              <h3>Order Items ({items.length})</h3>
              {items.map((item) => (
                <div className="public-item-row" key={item.id}>
                  <span>{item.product_name} × {item.quantity}</span>
                  <strong>Rs. {(Number(item.unit_price) * item.quantity).toLocaleString()}</strong>
                </div>
              ))}
              <div className="public-item-row public-total-row">
                <span>Total</span>
                <strong>Rs. {Number(order.total_amount).toLocaleString()}</strong>
              </div>
            </div>
            <div className="public-tracking-col">
              <h3>Shipping Info</h3>
              <div className="public-info-row"><span>Customer</span><strong>{order.customer_name}</strong></div>
              <div className="public-info-row"><span>District</span><strong>{order.shipping_district}</strong></div>
              <div className="public-info-row"><span>Shipping</span><strong>Rs. {Number(order.shipping_cost || 0).toLocaleString()}</strong></div>
              <div className="public-info-row">
                <span>Placed</span>
                <strong>{new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="public-tracking-footer">
          <p>Need help? Contact us via WhatsApp for assistance.</p>
          <a
            href="https://wa.me/94757895678"
            target="_blank"
            rel="noopener noreferrer"
            className="button secondary"
          >
            Contact Support
          </a>
        </div>
      </div>
    </main>
  );
}

export default PublicTracking;